import { chakra } from '@chakra-ui/react'

import { PREFERS_REDUCED_MOTION_QUERY } from 'src/components/constants'
import { SCROLL_BAR_WIDTH } from 'src/utils/dom'
import { isChromakeyModeActive } from 'src/utils/env'
import { shouldForwardProp } from 'src/utils/should-forward-prop'

export const AppWrapper = chakra('div', {
    baseStyle: {
        height: '100%',
        width: '100vw',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
})

type AppContentProps = {
    $isHeaderHidden: boolean
}
export const AppContent = chakra<'main', AppContentProps>('main', {
    baseStyle: ({ $isHeaderHidden, theme }) => ({
        position: 'relative',
        height: $isHeaderHidden ? '100%' : `calc(100% - ${theme.headerHeight}px)`,
    }),
    shouldForwardProp,
})

type LoaderContainerProps = {
    $isVisible: boolean
    $backgroundColor?: string
}
export const LoaderContainer = chakra<'div', LoaderContainerProps>('div', {
    baseStyle: ({ $isVisible, $backgroundColor }) => ({
        display: 'flex',
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        position: 'fixed',
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
        // Above app header (2) and transcript toolbar (1) so loading covers the full chrome consistently
        zIndex: 5,
        backgroundColor: $backgroundColor,
        opacity: $isVisible ? 1 : 0,
        visibility: $isVisible ? 'visible' : 'hidden',
        pointerEvents: $isVisible ? 'auto' : 'none',
        transition: 'opacity 180ms ease, visibility 180ms ease',
        [`@media ${PREFERS_REDUCED_MOTION_QUERY}`]: {
            transition: 'none',
        },
    }),
    shouldForwardProp,
})

export const LoaderContent = chakra('div', {
    baseStyle: {
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        gap: 12,
    },
})

export const LoaderLabel = chakra('div', {
    baseStyle: {
        fontSize: '14px',
        lineHeight: '20px',
        fontWeight: 500,
    },
})

export const TranscriptContentArea = chakra('div', {
    baseStyle: {
        position: 'relative',
        display: 'flex',
        flexDirection: isChromakeyModeActive ? 'row' : 'column',
        flex: 1,
        minHeight: 0,
    },
})

export const TranscriptPanelLoader = chakra('div', {
    baseStyle: {
        display: 'flex',
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 0,
    },
})

export const TranscriptViewerLayoutContainer = chakra('div', {
    baseStyle: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        minHeight: 0,
    },
})

export const TranscriptViewerContainer = chakra<'div'>('div', {
    baseStyle: ({ theme }) => ({
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        [`@container (max-width: ${theme.breakPoints.tabletMaxWidth}px)`]: {
            flexDirection: isChromakeyModeActive ? 'column' : 'column-reverse',
        },
        [`@container (max-width: ${theme.breakPoints.mobilePhoneMaxWidth}px)`]: {
            minWidth: '100%',
        },
    }),
    shouldForwardProp,
})

export const MediaPlayerWrapperOuter = chakra('div', {
    baseStyle: {
        padding: `16px ${SCROLL_BAR_WIDTH}px 16px 0`,
    },
})

type MediaPlayerWrapperInnerProps = {
    $transcriptParagraphWidth: number
}
export const MediaPlayerWrapperInner = chakra<'div', MediaPlayerWrapperInnerProps>('div', {
    baseStyle: ({ $transcriptParagraphWidth }) => ({
        width: '100%',
        maxWidth: `${$transcriptParagraphWidth}px`,
        minWidth: '360px',
        margin: '0 auto',
    }),
    shouldForwardProp,
})

/* The library Alert takes no style props, so its (flex) content and wrapped text are centered from the outside. */
export const CenteredAlertContainer = chakra('div', {
    baseStyle: {
        '& .chakra-alert': {
            justifyContent: 'center',
            textAlign: 'center',
        },
    },
})
